import { TDateField, THeadingTag, TImageField, TLinkField, TSitecoreContext, TStringField, VideoSitecoreField } from '../../../types/types';
import { isEmptyObject } from '../../../utils/checkEmptyObject';
import { TSocialMediaBarProps } from '../../@shared/socialMediaBar/types/socialMediaBar.types';
import {
  TCategory,
  TNewsDetailContext,
  TNewsDetailFields,
  TNewsDetailMediaListItem,
  TNewsDetailParams,
  TNewsDetailProps,
} from '../types/newsDetail.types';

export class NewsDetailModel {
  private readonly fields?: TNewsDetailFields;
  private readonly params?: TNewsDetailParams;
  private readonly context?: TNewsDetailContext;

  constructor(props: TNewsDetailProps) {
    this.fields = props.fields;
    this.params = props.params;
    this.context = props?.sitecoreContext as any || null;
  }

  get title(): TStringField | undefined {
    return this.fields?.title;
  }

  get headingTag(): THeadingTag {
    return (this.params?.headingTag?.toLowerCase() as THeadingTag) || 'h1';
  }

  get eyebrow(): TStringField | undefined {
    return this.fields?.eyebrow;
  }

  get newsDate(): TDateField | undefined {
    return this.fields?.newsDate;
  }

  get desktopImage(): TImageField | undefined {
    return this.fields?.image;
  }

  get mobileImage(): TImageField | undefined {
    return this.fields?.mobileImage;
  }

  get showVideo(): boolean {
    return !!this.fields?.useVideo?.value && !!this.fields?.video?.value?.href;
  }

  get video(): VideoSitecoreField {
    return this.fields?.video ?? { value: {} };
  }

  get tags(): TStringField | undefined {
    return this.fields?.tags;
  }

  get categories(): Array<TCategory> {
    if (this?.fields?.tagCategories?.length) {
      return this.fields?.tagCategories?.map((category) => {
        const redirectUrl = (this.fields?.newsMainPageLink?.value ?? '') + '/?' + category?.parent + '=' + category?.id;
        return {
          name: category?.name,
          href: encodeURI(redirectUrl),
        };
      });
    }
    return [];
  }

  get mediaExists(): boolean {
    return !!(this.showVideo || this.fields?.image?.value?.src || this.fields?.mobileImage?.value?.src);
  }

  get detailedDescription(): TStringField | undefined {
    return this.fields?.detailedDescription;
  }

  get mediaList(): TNewsDetailMediaListItem[] {
    return this.fields?.mediaList || [];
  }

  get primaryCta(): TLinkField | undefined {
    return this.fields?.primaryCta;
  }

  get showPrimaryCta(): boolean {
    return !!(this.primaryCta?.value?.text && this.primaryCta?.value?.href);
  }

  get secondaryCta(): TLinkField | undefined {
    return this.fields?.secondaryCta;
  }

  get showSecondaryCta(): boolean {
    return !!(this.secondaryCta?.value?.text && this.secondaryCta?.value?.href);
  }

  get showCtaWrapper(): boolean {
    return this.showPrimaryCta || this.showSecondaryCta;
  }

  get socialMediaBarProps(): TSocialMediaBarProps | undefined {
    return isEmptyObject(this.fields?.socialMediaBar) ? undefined : this.fields?.socialMediaBar;
  }

  get contextLanguage(): string {
    return this.context?.language || 'en-GB';
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }
}
